import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'

import ListTagCloudItem, {listTagcloudHeadingTags} from './mt-list-tagcloud-item.js'

const DEFAULT_TITLE = 'TAGCLOUD_DEFAULT_TITLE'

// eslint-disable-next-line react/prop-types
const linkFactoryDefaultProp = ({children, ...rest}) => <a {...rest}>{children}</a>

const _renderTag = linkFactory => (tagcloud, index) =>
  <ListTagCloudItem {...tagcloud} linkFactory={linkFactory} key={index} />

const ListTagcloud = ({
  headingTag,
  initialTagclouds = [],
  makeId,
  pageName = 'home',
  linkFactory = linkFactoryDefaultProp
}) => {
  const [tagclouds, setTagclouds] = useState(initialTagclouds)
  const {domain, i18n} = useContext(Context)
  const hasTagclouds = Boolean(tagclouds.length)

  const _translateTag = tagcloud => {
    return tagcloud.title
      ? {
          headingTag: tagcloud.headingTag,
          title: i18n.t(tagcloud.title, tagcloud.placeholders),
          tags: tagcloud.tags
        }
      : tagcloud
  }

  const _insertTitle = tags => ({
    title: DEFAULT_TITLE,
    tags
  })

  const _addTitleAttribute = (tagcloud, index) => {
    const {title, tags, attributes: {title: titleAttrLiteral} = {}, placeholders: {type} = {}} = tagcloud

    const tagsWithTitle = tags.map(tag => {
      const {label, title} = tag
      const titleLiteral = titleAttrLiteral ? i18n.t(titleAttrLiteral, {label, type}) : title
      return {...tag, title: titleLiteral}
    })

    return {...tagcloud, headingTag, title, tags: tagsWithTitle}
  }

  const _renderTagcloudsList = () => {
    const [tagcloudsWithTitle, tagcloudsWithoutTitle] = tagclouds.reduce(
      ([withTitle, withoutTitle], tag) => {
        tag.title ? withTitle.push(tag) : withoutTitle.push(...tag.tags)
        return [withTitle, withoutTitle]
      },
      [[], []]
    )

    const formattedTagcloud = tagcloudsWithoutTitle
      ? [...tagcloudsWithTitle, _insertTitle(tagcloudsWithoutTitle)]
      : [...tagcloudsWithTitle]

    return formattedTagcloud
      .filter(({tags}) => tags.length > 0)
      .map(_addTitleAttribute)
      .map(_translateTag)
      .map(_renderTag(linkFactory))
  }

  useMount(() => {
    const hasInitialTagclouds = Boolean(initialTagclouds.length)

    if (!hasInitialTagclouds && makeId) {
      domain
        .get('get_tagcloud_seo_use_case')
        .execute({pageName, makeId})
        .then(defaultTagclouds => setTagclouds(defaultTagclouds))
    }
  })

  useEffect(() => {
    if (initialTagclouds.length) {
      initialTagclouds && setTagclouds(initialTagclouds)
    }
  }, [initialTagclouds])

  return hasTagclouds ? <div className="mt-ListTagClouds">{_renderTagcloudsList()}</div> : null
}

ListTagcloud.propTypes = {
  headingTag: PropTypes.oneOf(listTagcloudHeadingTags),
  initialTagclouds: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      tags: PropTypes.array.isRequired
    })
  ),
  linkFactory: PropTypes.func,
  makeId: PropTypes.number,
  pageName: PropTypes.string
}

ListTagcloud.displayName = 'ListTagcloud'

export {listTagcloudHeadingTags}

export default ListTagcloud
